import api from '../../../services/api';
import axios from 'axios';
import { checkToken } from '../../../utils/lambdaUtils';
import logger from '@/utils/logger';
import { DocumentModel } from '@/models/DocumentModel';
import { useAuthStore } from '@/stores/authStore';


const baseUrl = `${import.meta.env.VITE_APP_API_BASE_URL}/hp/`;

const normalizedId = (id, errorMessage) => {
  const normalizedId = Number(id);

  if (
    normalizedId == null ||
    Number.isNaN(normalizedId) ||
    !Number.isInteger(normalizedId) ||
    normalizedId < 1
  ) {
    throw new Error(errorMessage || 'Missing or invalid id');
  }

  return normalizedId;
};

/**
 * Fetches the annual report for a given report ID, organization ID, and period ID.
 *
 * @param {string} reportId - The ID of the report.
 * @returns {Promise<Object>} The data of the annual report.
 * @throws {Error} If any of the required parameters are missing or if there is an error fetching the report.
 */
export const getAnnualReport = async (reportId) => {
  const id = normalizedId(reportId, 'Invalid report ID');

  try {
    await checkToken();
    const response = await api.get(`${baseUrl}annualreport?reportId=${encodeURIComponent(id)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting annual report', error);
    throw error;
  }
};

/**
 * Retrieves HP report status history for a given report ID.
 * @param {string} reportId - The ID of the HP report.
 * @returns {Promise<Object>} The response data containing HP report status history.
 * @throws {Error} If the required parameter is missing or if there is an error retrieving the status history.
 */
export const getAnnualReportStatusHistory = async (reportId) => {
    const id = normalizedId(reportId, 'Invalid report ID');
    try {
      await checkToken();
      const response = await api.get(`${baseUrl}annualreportstatushistory?id=${encodeURIComponent(id)}`);
      if (Array.isArray(response.data)) {
        return response.data;
      } else {
        throw new Error('Invalid response data');
      }
    } catch (error) {
      logger.error('Error getting annual report status history', error);
      throw error;
    }
};

export const createAnnualReport = async (organizationId, periodId, contactId, roleId) => {
  const orgId = normalizedId(organizationId, 'Invalid organization ID');
  const perId = normalizedId(periodId, 'Invalid period ID');
  const contId = normalizedId(contactId, 'Invalid contact ID');

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}createannualreport`, { organizationId: orgId, periodId: perId, contactId: contId, roleId });
    return response.data;
  } catch (error) {
    logger.error('Error creating annual report', error);
  }
};

export const getAnnualReportContacts = async (reportId) => {
  const id = normalizedId(reportId, 'Invalid report ID'); 
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}annualreportcontacts?id=${encodeURIComponent(id)}`);
    if (Array.isArray(response.data)) {
      return response.data;
    } else {
      throw new Error('Invalid response data');
    }
  } catch (error) {
    logger.error('Error getting annual report contacts', error);
    throw error;
  }
};

export const getAvailableReportContacts = async (reportId) => {
  const id = normalizedId(reportId, 'Invalid report ID');
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}availablereportcontacts?id=${encodeURIComponent(id)}`);
    if (Array.isArray(response.data)) {
      return response.data;
    } else {
      throw new Error('Invalid response data');
    }
  } catch (error) {
    logger.error('Error getting available report contacts', error);
    throw error;
  }
};

export const addReportContact = async (reportId, contactId, roleId) => {
  const id = normalizedId(reportId, 'Invalid report ID');
  const contId = normalizedId(contactId, 'Invalid contact ID');
  const rId = normalizedId(roleId, 'Invalid role ID');

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}addreportcontact?id=${encodeURIComponent(id)}`, { contactId: contId, roleId: rId });
    return response.data;
  } catch (error) {
    logger.error('Error adding report contact', error);
    throw error;
  }
};

export const addReportContacts = async (reportId, contacts) => {
  const id = normalizedId(reportId, 'Invalid report ID');

    if (!Array.isArray(contacts) || contacts.length === 0) {
    throw new Error('Missing or invalid required parameters');
  }  
  try {
    const sanitizedContacts = contacts
      .filter(({ roleId }) => roleId != null && roleId !== 0)
      .map(({ contactId, roleId }) => ({ contactId, roleId }));
    await checkToken();
    const response = await api.post(`${baseUrl}addreportcontacts?id=${encodeURIComponent(id)}`, sanitizedContacts);
    return response.data;
  } catch (error) {
    logger.error('Error adding report contacts', error);
    throw error;
  }
};

export const updateReportContact = async (reportId, updateContact) => {
  if (!reportId || !updateContact) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updatereportcontact?id=${encodeURIComponent(reportId)}`, updateContact);
    return response.data;
  } catch (error) {
    logger.error('Error updating report contact', error);
    throw error;
  }
};

export const removeReportContact = async (reportId, contactId) => {
  if (!reportId || !contactId) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}removereportcontact?id=${encodeURIComponent(reportId)}&contactId=${encodeURIComponent(contactId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error removing report contact', error);
    throw error;
  }
};

export const getAnnualReportDocuments = async (reportId) => {
  const id = normalizedId(reportId, 'Invalid report ID');
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}annualreportdocuments?id=${encodeURIComponent(id)}`);
    if (Array.isArray(response.data)) {
      const docs = response.data.map(doc => new DocumentModel(doc));
      return docs;
    } else {
      throw new Error('Invalid response data');
    }
  } catch (error) {
    logger.error('Error getting annual report documents', error);
    throw error;
  }
};

const transformDocument = (document) => {
  return {
    request: {
      filename: document.fileName,
      filetype: document.fileType,
      filesize: document.fileSize,
      documenttypeid: document.documentTypeId,
    },
  };
};


export const attachReportDocuments = async (reportId, documents) => {
  const id = normalizedId(reportId, 'Invalid report ID');

  if (!Array.isArray(documents) || documents.length === 0) {
    throw new Error('Missing or invalid required parameters');
  }  

  try {

    await checkToken();
    const store = useAuthStore();
    const uploadPromises = documents.map(async (doc) => {
      if (!doc.file ) {
        throw new Error('Missing file for a document');
      }

      const transformedData = transformDocument(doc);
      const formData = new FormData();
      formData.append('file', doc.file);
      formData.append('request', JSON.stringify(transformedData.request));

      const response = await axios.post(
        `${baseUrl}attachreportdocument?id=${encodeURIComponent(id)}`,
        formData,
        { 
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${store.token}`,
          },
        }
      );

      return response.data;
    });
    const results = await Promise.all(uploadPromises);
    return results;
  } catch (error) {
    logger.error('Error attaching report document', error);
    throw error;
  }
};

export const removeReportDocument = async (reportId, documentId) => {
  const id = normalizedId(reportId, 'Invalid report ID');
  const docId = normalizedId(documentId, 'Invalid document ID');

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}removereportdocument?id=${encodeURIComponent(id)}&documentId=${encodeURIComponent(docId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error removing report document', error);
    throw error;
  }
};

export const getSectionComment = async(reportId, sectionId) => {
  const id = normalizedId(reportId, 'Invalid report ID');
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}sectioncomment?reportId=${encodeURIComponent(id)}&sectionId=${encodeURIComponent(sectionId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting section comment', error);
    throw error;
  }
};

export const updateSectionComment = async(reportId, sectionId, comment) => {
  const id = normalizedId(reportId, 'Invalid report ID');
  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updatesectioncomment`, { reportId: id, sectionId, comment });
    return response.data;
  } catch (error) {
    logger.error('Error updating section comment', error);
    throw error;
  }
};

export const getPremiums = async(reportId) => {
  const id = normalizedId(reportId, 'Invalid report ID');
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}premiums?reportId=${encodeURIComponent(id)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting premium', error);
    throw error;
  }
};

export const updatePremiums = async(data) => {
  if (!data) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updatepremiums`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating premium', error);
    throw error;
  }
};

export const updateReportStatus = async(reportId, statusId, isCertified) => {
  try {
    await checkToken();
    const data = {
      reportId,
      reportStatusId: statusId,
      isCertified,
    };
    const response = await api.post(`${baseUrl}updatereportstatus`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating report status', error);
    throw error;
  }
};

export const getReportSectionStatuses = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}reportsectionstatuses?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting report section statuses', error);
    throw error;
  }
};

export const getMedicalEnrollmentMetrics = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}medicalenrollmentmetrics?id=${encodeURIComponent(reportId)}`);
    return response.data;

  } catch (error) {
    logger.error('Error getting medical enrollment metrics', error);
    throw error;
  }
};

export const getDentalEnrollmentMetrics = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}dentalenrollmentmetrics?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting dental enrollment metrics', error);
    throw error;
  }
};

export const getRevenueSourceMetrics = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}revenuesourcemetrics?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting revenue source metrics', error);
    throw error;
  }
};

export const getExpenseMetrics = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}expensemetrics?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting expense metrics', error);
    throw error;
  }
};

export const getMedicalEnrollmentCalculation = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}medicalenrollmentcalculation?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting medical enrollment calculation', error);
    throw error;
  }
};

export const getDentalEnrollmentCalculation = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}dentalenrollmentcalculation?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting dental enrollment calculation', error);
    throw error;
  }
};

export const updateMedicalEnrollmentMetrics = async(reportId, data) => {
  if (!reportId || !data) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updatemedicalenrollmentmetrics?id=${encodeURIComponent(reportId)}`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating medical enrollment metrics', error);
    throw error;
  }
};

export const updateDentalEnrollmentMetrics = async(reportId, data) => {
  if (!reportId || !data) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updatedentalenrollmentmetrics?id=${encodeURIComponent(reportId)}`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating dental enrollment metrics', error);
    throw error;
  }
};

export const updateMedicalEnrollmentCalculation = async(reportId, data) => {
  if (!reportId || !data) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updatemedicalenrollmentcalculation?id=${encodeURIComponent(reportId)}`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating medical enrollment calculation', error);
    throw error;
  }
};

export const updateDentalEnrollmentCalculation = async(reportId, data) => {
  if (!reportId || !data) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updatedentalenrollmentcalculation?id=${encodeURIComponent(reportId)}`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating dental enrollment calculation', error);
    throw error;
  }
};

export const updateRevenueSourceMetrics = async(reportId, data) => {
  if (!reportId || !data) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updaterevenuesourcemetrics?id=${encodeURIComponent(reportId)}`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating revenue source metrics', error);
    throw error;
  }
};

export const updateExpenseMetrics = async(reportId, data) => {
  if (!reportId || !data) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updateexpensemetrics?id=${encodeURIComponent(reportId)}`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating expense metrics', error);
    throw error;
  }
};

export const getExpenseMetricsPriorYearPmpm = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}expensemetricsprioryearpmpm?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting expense metrics prior year PMPM', error);
    throw error;
  }
};

export const getIndirectExpenseMetrics = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}indirectexpensemetrics?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting indirect expense metrics', error);
    throw error;
  }
};

export const updateIndirectExpenseMetrics = async(reportId, data) => {
  if (!reportId || !data) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updateindirectexpensemetrics?id=${encodeURIComponent(reportId)}`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating indirect expense metrics', error);
    throw error;
  }
};

export const getCapitalCostMetrics = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}capitalcostmetrics?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting capital cost metrics', error);
    throw error;
  }
};

export const updateCapitalCostMetrics = async(reportId, data) => {
  if (!reportId || !data) {
    throw new Error('Missing required parameters');
  }

  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updatecapitalcostmetrics?id=${encodeURIComponent(reportId)}`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating capital cost metrics', error);
    throw error;
  }
};

export const getAcaOtherExpense = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}acaotherexpense?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting ACA and other expenses', error);
    throw error;
  }
};

export const updateAcaOtherExpense = async(reportId, data) => {
  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updateacaotherexpense?id=${encodeURIComponent(reportId)}`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating ACA and other expenses', error);
    throw error;
  }
};

export const getTaxAssessment = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}taxassessment?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting tax assessments', error);
    throw error;
  }
};

export const updateTaxAssessment = async(reportId, data) => {
  try {
    await checkToken();
    const response = await api.post(`${baseUrl}updatetaxassessment?id=${encodeURIComponent(reportId)}`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating tax assessments', error);
    throw error;
  }
};

export const getMpspParticipation = async(reportId) => {
  try {
    await checkToken();
    const response = await api.get(`${baseUrl}mpspparticipation?id=${encodeURIComponent(reportId)}`);
    return response.data;
  } catch (error) {
    logger.error('Error getting MPSP participation', error);
    throw error;
  }
};

export const updateMpspParticipation = async(reportId, isParticipant) => {
  try {
    await checkToken();
    const data = {
      isMpspParticipant: isParticipant,
    };
    const response = await api.post(`${baseUrl}updatempspparticipation?id=${encodeURIComponent(reportId)}`, data);
    return response.data;
  } catch (error) {
    logger.error('Error updating MPSP participation', error);
    throw error;
  }
};